import { useMemo } from 'react'
import { Trans } from 'react-i18next'

import { PageType } from '@makyoapp/makyo-sdk/models/AppBuilder'
import { VariableResultMenu } from '@makyoapp/makyo-common'
import { Moon24, Sun24 } from '@makyoapp/makyo-icon/icon'
import { ColorVariant } from '@makyoapp/makyo-tw-ui'
import { Field, Switch } from '@makyoapp/makyo-ui'

import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useBuilderColorSchemeV2 } from '@/hooks/__project__/useBuilderColorSchemeV2'
import { AccordionItem } from '@/components/Accordion/Accordion'
import { BrandKitColorsDropdown } from '@/components/BrandKitColorsDropdown'
import { MenuSelectV2 } from '@/components/Builder/MenuSelectV2'

type VisibilityType = 'visibility' | 'showIcon' | 'showTitle'

/**
 * TopBarAccordionItem is a functional component that represents an accordion item in the top bar.
 * It provides an interactive UI item that can be expanded or collapsed to display or hide content.
 */
function TopBarAccordionItem() {
  const {
    appClient,
    project,
    pages,
    topBarColor,
    setTopBarColor,
    updateProjectNavBarColor,
    updateProjectNavBarVisibility,
    topBarVisibility,
    setTopBarVisibility,
    topBarShowIcon,
    setTopBarShowIcon,
    topBarShowTitle,
    setTopBarShowTitle,
  } = useAppBuilderContext()

  const [scheme] = useBuilderColorSchemeV2()
  const mode = scheme.current

  const handleUpdateBgColor = async (mode: 'light' | 'dark', color: ColorVariant) => {
    if (!project?.id || !color) return

    setTopBarColor((prev) => {
      prev[mode] = color
      updateProjectNavBarColor(project.id, prev.light, prev.dark)
      return prev
    })
  }

  const handleUpdateVisibility = async (value: boolean, type: VisibilityType) => {
    if (!project?.id) return

    const setter = visibilitySetters[type]

    try {
      setter(value)

      const params = {
        visibility: type === 'visibility' ? value : undefined,
        iconVisibility: type === 'showIcon' ? value : undefined,
        titleVisibility: type === 'showTitle' ? value : undefined,
      }

      await updateProjectNavBarVisibility(
        params.visibility ?? project?.topBarVisibility ?? true,
        params.iconVisibility,
        params.titleVisibility
      )
    } catch (error) {
      setter(!value)
    }
  }

  const visibilitySetters = {
    visibility: setTopBarVisibility,
    showIcon: setTopBarShowIcon,
    showTitle: setTopBarShowTitle,
  } as const

  // currently we dont have a place to store global top bar menu in sdk,
  // so we will store it in the pre-defined page (user profile page)
  const trailingMenu = useMemo(() => {
    const userProfilePage = pages.find((p) => p.type === PageType.UserProfile)
    const userProfileTrailingMenu = (userProfilePage?.tbTrailingElements[0] ?? []) as VariableResultMenu
    return userProfileTrailingMenu?.value || ''
  }, [pages])

  return (
    <AccordionItem
      itemId='setting-top-bar'
      title={<Trans>Top Navigation Bar</Trans>}
      righSection={mode === 'dark' ? <Moon24 className='w-3.5 h-3.5' /> : <Sun24 className='w-3.5 h-3.5' />}
    >
      <Field isVertical className='py-0 flex items-center! flex-row! justify-between! min-h-10!'>
        <label className='w-5/12'>
          <Trans>Show Bar</Trans>
        </label>
        <Switch checked={topBarVisibility} onClick={(value) => handleUpdateVisibility(value, 'visibility')} />
      </Field>
      <Field isVertical className='py-0 flex items-center! flex-row! justify-between! min-h-10!'>
        <label className='w-5/12'>
          <Trans>Show Icon</Trans>
        </label>
        <Switch checked={topBarShowIcon} onClick={(value) => handleUpdateVisibility(value, 'showIcon')} />
      </Field>
      <Field isVertical className='py-0 flex items-center! flex-row! justify-between! min-h-10!'>
        <label className='w-5/12'>
          <Trans>Show Title</Trans>
        </label>
        <Switch checked={topBarShowTitle} onClick={(value) => handleUpdateVisibility(value, 'showTitle')} />
      </Field>
      <Field isVertical className='py-0 flex items-center! flex-row! justify-between! min-h-10!'>
        <label className='w-4/12'>
          <Trans>Menu</Trans>
        </label>

        <MenuSelectV2
          value={trailingMenu}
          onChange={async (menuId) => {
            await appClient.UpdateGlobalTopbarMenu(menuId)
          }}
        />
      </Field>

      <Field isVertical className='py-0 flex items-center! flex-row! justify-between! min-h-10!'>
        <label className='w-4/12'>
          <Trans>Color {mode === 'dark' ? '(Dark)' : '(Light)'}</Trans>
        </label>
        <BrandKitColorsDropdown
          value={mode === 'dark' ? topBarColor.dark : topBarColor.light}
          onChange={(value) => handleUpdateBgColor(mode, value)}
          showExtendedColors
        />
      </Field>
    </AccordionItem>
  )
}

export default TopBarAccordionItem
